import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, getDocs, collection, query, where, limit, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider, appleProvider } from './config';

const checkUsernameAvailable = async (username) => {
  const q = query(collection(db, 'users'), where('username', '==', username.toLowerCase()), limit(1));
  const snap = await getDocs(q);
  return snap.empty;
};

export const signUpWithEmail = async (email, password, username) => {
  const isAvailable = await checkUsernameAvailable(username);
  if (!isAvailable) throw new Error('Username is already taken');

  const { user } = await createUserWithEmailAndPassword(auth, email, password);
  await setDoc(doc(db, 'users', user.uid), {
    username: username.toLowerCase(),
    displayName: username,
    bio: '',
    avatarUrl: '',
    createdAt: serverTimestamp()
  });
  await setDoc(doc(db, 'users', user.uid, 'private', 'data'), { email });
  return user;
};

export const signInWithEmail = async (email, password) => {
  const { user } = await signInWithEmailAndPassword(auth, email, password);
  return user;
};

export const signInWithGoogle = async () => {
  const { user } = await signInWithPopup(auth, googleProvider);
  const userDoc = await getDoc(doc(db, 'users', user.uid));
  if (!userDoc.exists()) {
    let base = user.email.split('@')[0].toLowerCase().replace(/[^a-z0-9_]/g, '');
    let username = base;
    let i = 1;
    while (!(await checkUsernameAvailable(username))) {
      username = `${base}${i++}`;
      if (i > 1000) { username = `user_${Date.now()}`; break; }
    }
    await setDoc(doc(db, 'users', user.uid), {
      username,
      displayName: user.displayName || username,
      bio: '',
      avatarUrl: user.photoURL || '',
      createdAt: serverTimestamp()
    });
    await setDoc(doc(db, 'users', user.uid, 'private', 'data'), { email: user.email });
  }
  return user;
};

export const signInWithApple = async () => {
  const { user } = await signInWithPopup(auth, appleProvider);
  const userDoc = await getDoc(doc(db, 'users', user.uid));
  if (!userDoc.exists()) {
    const email = user.email || '';
    let base = email ? email.split('@')[0].toLowerCase().replace(/[^a-z0-9_]/g, '') : 'user';
    let username = base;
    let i = 1;
    while (!(await checkUsernameAvailable(username))) {
      username = `${base}${i++}`;
      if (i > 1000) { username = `user_${Date.now()}`; break; }
    }
    await setDoc(doc(db, 'users', user.uid), {
      username,
      displayName: user.displayName || username,
      bio: '',
      avatarUrl: '',
      createdAt: serverTimestamp()
    });
    if (email) await setDoc(doc(db, 'users', user.uid, 'private', 'data'), { email });
  }
  return user;
};

export const logOut = async () => signOut(auth);
export const onAuthChange = (cb) => onAuthStateChanged(auth, cb);
export const getCurrentUser = () => auth.currentUser;
